import {
  getDocumentList,
  invalidateDocumentListData,
  prepareDocumentForSave,
} from "@prismicio/editor-fields"
import { Button, Tooltip, useAlert } from "@prismicio/editor-ui"
import type { StaticCustomType } from "@prismicio/types-internal/lib/customtypes"
import { useQueryClient } from "@tanstack/react-query"

import { env } from "../../../app/env"
import { baseUrl } from "../../../app/services/core"
import {
  getReleaseId,
  invalidateReleaseSizeData,
} from "../../../app/services/release"
import { invalidateRepositoryTags } from "../../../app/services/repository"
import { useRole } from "../../../app/services/user"
import { createDocument } from "../../documentData"
import { useNewDocument } from "../../DocumentProvider"
import { useGoToDocument } from "../../documentRouting"
import { validateUID } from "../validateUID"
import { BlockNavigationOnUnsavedChangesPrompt } from "./BlockNavigationOnUnsavedChangesPrompt"
import { actionAlert, type PendingActionProps } from "./HeaderButtons"

interface CreateButtonProps extends PendingActionProps {}

export function CreateButton(props: CreateButtonProps) {
  const { pendingAction } = props
  const { disabled, action, userCanCreate } = useCreateButton(props)
  return (
    <>
      <Tooltip
        visible={!userCanCreate}
        title="You can't create documents"
        content="Only users with the role of publisher or administrator can create documents."
      >
        <Button
          size="large"
          color="grey"
          loading={pendingAction === "create"}
          disabled={disabled}
          onClick={action}
        >
          Save
        </Button>
      </Tooltip>
      <BlockNavigationOnUnsavedChangesPrompt
        // enable the prompt if the create button is not disabled
        // this means that there have been changes to the document
        enabled={!disabled}
        action={useCreateButton}
      />
    </>
  )
}

export function useCreateButton(props: CreateButtonProps) {
  const { pendingAction, onPendingActionChange } = props

  const {
    locale,
    groupLangId,
    customType,
    releaseId,
    content,
    edited,
    documentTitle,
    draft,
  } = useNewDocument()

  const queryClient = useQueryClient()

  const { isContributor } = useRole(locale)

  const userCanCreate = !isContributor

  const disabled = !edited || Boolean(pendingAction) || !userCanCreate

  const { showAlert } = useAlert()
  const goToDocument = useGoToDocument()

  async function create() {
    onPendingActionChange("create")

    const preparedDocument = prepareDocumentForSave(customType, content)

    const isValidUID = await validateUID({
      content: preparedDocument,
      languageId: locale,
      customType,
      showAlert,
      pendingAction: "create",
      onRetry: create,
    })

    if (!isValidUID) {
      onPendingActionChange(undefined)
      return
    }

    const singletonGroupLangID = await getSingletonGroupLangID(customType)

    const tags = [...draft.documentTags]

    try {
      const newDocument = await createDocument({
        customTypeId: customType.id,
        releaseId: releaseId,
        content: preparedDocument,
        title: documentTitle.draftTitle,
        groupLangId: singletonGroupLangID ?? groupLangId,
        locale,
        tags,
      })

      const status = releaseId ? "release" : "unclassified"
      const newDraft = newDocument.versions.find(
        (version) => version.status === status,
      )

      if (releaseId) {
        // When creating a new document in a release, that particular release's size will be incremented.
        void invalidateReleaseSizeData(releaseId)
      }

      void invalidateDocumentListData(queryClient)

      // Invalidate tags as some might have been created in the process
      invalidateRepositoryTags()

      if (newDraft) {
        goToDocument({
          id: newDocument.id,
          status: newDraft.status,
          releaseId: getReleaseId(newDraft),
          replace: true,
        })
      }
    } catch {
      actionAlert({ action: "create", showAlert, onRetry: create })
    } finally {
      onPendingActionChange(undefined)
    }
  }
  return { disabled, action: create, userCanCreate }
}

// Checks for matching singleton custom type docs across all locales
// If a doc with the same singleton custom type exists in another locale
// then we need to grab it's group lang id and pair it to the doc we are creating
async function getSingletonGroupLangID(customType: StaticCustomType) {
  // if the custom type is NOT singleton then we will already have a group lang id
  if (customType.repeatable) return undefined

  const limit = 50

  try {
    const response = await getDocumentList({
      baseUrl,
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
      customTypes: [customType.id],
      limit,
    })
    return response.results[0]?.group_lang_id
  } catch {
    return undefined
  }
}
